import type { Box } from './routeShape';

/** Low-level canvas helpers shared by the sticker templates. */

export function roundRectPath(ctx: CanvasRenderingContext2D, box: Box, radius: number): void {
  const r = Math.min(radius, box.w / 2, box.h / 2);
  ctx.beginPath();
  ctx.moveTo(box.x + r, box.y);
  ctx.arcTo(box.x + box.w, box.y, box.x + box.w, box.y + box.h, r);
  ctx.arcTo(box.x + box.w, box.y + box.h, box.x, box.y + box.h, r);
  ctx.arcTo(box.x, box.y + box.h, box.x, box.y, r);
  ctx.arcTo(box.x, box.y, box.x + box.w, box.y, r);
  ctx.closePath();
}

export function fillVertical(
  ctx: CanvasRenderingContext2D,
  box: Box,
  stops: [number, string][],
): void {
  const gradient = ctx.createLinearGradient(box.x, box.y, box.x, box.y + box.h);
  for (const [at, color] of stops) gradient.addColorStop(at, color);
  ctx.fillStyle = gradient;
  ctx.fillRect(box.x, box.y, box.w, box.h);
}

/**
 * A blurred copy of what's already been painted, by downscaling and drawing it
 * back up. `ctx.filter` would be one line, but it's still uneven across the
 * browsers this runs in — bilinear smoothing is not, and at this radius the two
 * are indistinguishable.
 */
export function blurredCopy(source: HTMLCanvasElement, downscale = 14): HTMLCanvasElement {
  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.round(source.width / downscale));
  small.height = Math.max(1, Math.round(source.height / downscale));
  const smallCtx = small.getContext('2d');
  if (smallCtx) {
    smallCtx.imageSmoothingQuality = 'high';
    smallCtx.drawImage(source, 0, 0, small.width, small.height);
  }

  const blurred = document.createElement('canvas');
  blurred.width = source.width;
  blurred.height = source.height;
  const blurredCtx = blurred.getContext('2d');
  if (blurredCtx) {
    blurredCtx.imageSmoothingQuality = 'high';
    blurredCtx.drawImage(small, 0, 0, blurred.width, blurred.height);
  }
  return blurred;
}

/** Frosted glass: the backdrop blurred behind a translucent, hairline-lit panel. */
export function frostedPanel(
  ctx: CanvasRenderingContext2D,
  backdrop: HTMLCanvasElement,
  box: Box,
  radius: number,
  tint: string,
): void {
  ctx.save();
  roundRectPath(ctx, box, radius);
  ctx.clip();
  ctx.drawImage(backdrop, 0, 0);
  ctx.fillStyle = tint;
  ctx.fillRect(box.x, box.y, box.w, box.h);
  ctx.restore();

  roundRectPath(ctx, box, radius);
  ctx.strokeStyle = 'rgba(255,255,255,0.38)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

export function measureTracked(ctx: CanvasRenderingContext2D, text: string, tracking: number): number {
  let width = 0;
  for (const char of text) width += ctx.measureText(char).width + tracking;
  return width - (text.length ? tracking : 0);
}

/**
 * Letter-spaced text. `ctx.letterSpacing` exists but silently does nothing in
 * some of the browsers we support, which would misalign the labels it's used
 * for, so the tracking is applied glyph by glyph.
 */
export function drawTracked(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  tracking: number,
  align: 'left' | 'center' = 'left',
): void {
  let cursor = align === 'center' ? x - measureTracked(ctx, text, tracking) / 2 : x;
  for (const char of text) {
    ctx.fillText(char, cursor, y);
    cursor += ctx.measureText(char).width + tracking;
  }
}

/** Word wrap against the current font, ellipsising whatever doesn't fit. */
export function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !line) {
      line = candidate;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && line) lines.push(line);

  if (lines.length === maxLines) {
    const consumed = lines.join(' ').split(/\s+/).length;
    if (consumed < words.length) lines[maxLines - 1] = ellipsise(ctx, lines[maxLines - 1], maxWidth);
  }
  return lines;
}

function ellipsise(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1).trimEnd();
  }
  return `${out}…`;
}

export function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  return ctx.measureText(text).width <= maxWidth ? text : ellipsise(ctx, text, maxWidth);
}
